import { useEffect, useMemo } from "react";

import { useSpecificationsSharetokenPostV2 } from "@btnet/services-btnet/handlers/specifications/specifications";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { routes } from "utilities/routes";

import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";

const GenerateQRCodeModal = lazyLoadComponent(
    () => import("commonComponents/entity/sharing/GenerateQRCodeModal"),
);

export interface IBTSpecificationShareQRCodeProps {
    specificationTitle: string;
    externalId: string;
    jobId?: number;
    modalConfig?: IModalConfiguration;
}

function BTSpecificationShareQRCodeInternal({
    specificationTitle,
    externalId,
    jobId,
    modalConfig,
}: Readonly<IBTSpecificationShareQRCodeProps>) {
    const { mutate: fetchShareToken, data: shareTokenResponse } = useSpecificationsSharetokenPostV2(
        {
            mutation: {
                onError: (e) => showAPIErrorMessage(e),
            },
        },
    );

    useEffect(() => {
        fetchShareToken({ params: { jobIdParam: jobId } });
    }, [jobId, fetchShareToken]);

    const shareUrl = useMemo(() => {
        const token = shareTokenResponse?.data.shareToken;
        return token
            ? `${window.location.origin}${routes.appBase}${routes.specification.getExternalLink(token, externalId)}`
            : null;
    }, [shareTokenResponse, externalId]);

    const exportHeader = shareTokenResponse?.data.exportHeader;

    return (
        <>
            {shareUrl && (
                <GenerateQRCodeModal
                    exportHeader={exportHeader}
                    shareUrl={shareUrl}
                    title={specificationTitle}
                    modalConfig={modalConfig}
                    bottomDescriptionMarkup={
                        <>
                            Use your phone's camera to open this in a browser.
                            <br />
                            Anyone with the link can view this spec and the rest of the published
                            specs.
                        </>
                    }
                />
            )}
        </>
    );
}

const BTSpecificationShareQRCode = withErrorBoundary(BTSpecificationShareQRCodeInternal)(
    "Could not load BTSpecificationShareQRCode",
);
export default BTSpecificationShareQRCode;
